
import React, { createContext, useContext, useState, useEffect } from "react";
import { User } from "@/types";
import { toast } from "sonner";

type AuthContextType = {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Mock user data
const mockUsers = [
  {
    id: "1",
    name: "John Doe",
    email: "john@example.com",
    organisation: "ABC Construction",
    role: "admin" as const,
  },
  {
    id: "2",
    name: "Jane Smith",
    email: "jane@example.com",
    organisation: "XYZ Consultants",
    role: "editor" as const,
  },
];

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Track initial loading state with a shorter timeout
  const [isLoading, setIsLoading] = useState(true);
  
  // Initialize state from localStorage if available
  const [user, setUser] = useState<User | null>(() => {
    try {
      console.log("AuthProvider: Initializing from localStorage");
      
      // Check if localStorage is available
      if (typeof localStorage === 'undefined') {
        console.error("AuthProvider: localStorage is not available");
        return null;
      }
      
      const savedUser = localStorage.getItem('stratafold_user');
      console.log("AuthProvider: localStorage data exists:", !!savedUser);
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (error) {
      console.error("AuthProvider: Error reading from localStorage:", error);
      toast.error("Error accessing browser storage. Please check your browser settings.");
      return null;
    }
  });

  // Update localStorage when user changes and handle initial load completion
  useEffect(() => {
    const saveUserData = () => {
      try {
        // Check if localStorage is available
        if (typeof localStorage === 'undefined') {
          console.error("AuthProvider: localStorage is not available");
          return;
        }
        
        if (user) {
          localStorage.setItem('stratafold_user', JSON.stringify(user));
          console.log("AuthProvider: User authenticated and saved to localStorage");
        } else {
          localStorage.removeItem('stratafold_user');
          console.log("AuthProvider: User not authenticated, localStorage cleared");
        }
      } catch (error) {
        console.error("AuthProvider: Error writing to localStorage:", error);
        toast.error("Unable to save authentication state");
      }
    };

    // Try to save the user data
    saveUserData();
    
    // Mark loading as complete after a much shorter delay
    const timer = setTimeout(() => {
      setIsLoading(false);
      console.log("AuthProvider: Finished loading, isAuthenticated:", !!user);
    }, 500); // Reduced from 2000ms to 500ms
    
    return () => clearTimeout(timer);
  }, [user]);

  const login = async (email: string, password: string) => {
    console.log("AuthProvider: Login attempt for:", email);
    // Simulate API call with a reasonable delay
    return new Promise<void>((resolve, reject) => {
      setTimeout(() => {
        try {
          const foundUser = mockUsers.find((u) => u.email === email);
          if (foundUser && password === "password") {
            console.log("AuthProvider: Login successful for:", email);
            setUser(foundUser);
            resolve();
          } else {
            console.error("AuthProvider: Login failed for:", email, "- invalid credentials");
            reject(new Error("Invalid email or password"));
          }
        } catch (error) {
          console.error("AuthProvider: Unexpected error during login:", error);
          reject(new Error("An unexpected error occurred"));
        }
      }, 800); // Reduced from 1500ms to 800ms
    });
  };

  const logout = () => {
    console.log("AuthProvider: User logged out");
    setUser(null);
  };

  // Provide auth context value
  const contextValue = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    isLoading,
  };

  console.log("AuthProvider rendering, isAuthenticated:", !!user, "isLoading:", isLoading);
  
  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    console.error("useAuth must be used within an AuthProvider");
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
